/**
 * MKS-349 — API client for the accounting-import surface.
 *
 * Types mirror `backend/src/api/schemas/invoiceImport.ts` field for field. The
 * two upload calls go through raw `fetch` rather than `apiPost`, the same way
 * `adminImports.ts` does: the body is `FormData` and `apiPost` sets a JSON
 * content type, which breaks the multipart boundary.
 */
import {
  apiPost,
  API_BASE_URL,
  getBaseAuthHeaders,
  ApiClientError,
  type ApiError,
} from './client';

export const IMPORT_FIELDS = [
  'invoiceNumber',
  'clientName',
  'clientId',
  'issueDate',
  'dueDate',
  'netAmount',
  'grossAmount',
  'currency',
  'paidDate',
  'reference',
] as const;

export type ImportField = (typeof IMPORT_FIELDS)[number];

/** Which file column feeds each field. An absent key means "not mapped". */
export type ColumnMapping = Partial<Record<ImportField, string>>;

export type AmountSource = 'net' | 'gross';

export interface MatchOptions {
  amountTolerance: number;
  amountSource: AmountSource;
  vatRatePercent: number;
}

export type MatchConfidence = 'exact' | 'probable' | 'ambiguous' | 'none';

export interface AnalyzeResult {
  headers: string[];
  rowCount: number;
  mapping: ColumnMapping;
  options: MatchOptions;
  sampleRows: string[][];
}

export interface MatchCandidate {
  invoiceId: number;
  invoiceName: string | null;
  from: string | null;
  to: string | null;
  invoicedAmount: number;
  amountDifference: number;
  dateMatch: boolean;
  clientMatch: 'name' | 'alias';
}

export interface MatchRow {
  row: number;
  invoiceNumber: string | null;
  clientName: string | null;
  issueDate: string | null;
  dueDate: string | null;
  paidDate: string | null;
  reference: string | null;
  currency: string | null;
  netAmount: number | null;
  confidence: MatchConfidence;
  matchedInvoiceId: number | null;
  alreadyApplied: boolean;
  problem: string | null;
  candidates: MatchCandidate[];
}

export interface ImportInvoiceView {
  id: number;
  name: string | null;
  invoiceNumber: string | null;
  from: string | null;
  to: string | null;
  invoicedAmount: number;
  dueDate: string | null;
  paidAt: string | null;
  customerNames: string[];
}

export interface MatchResult {
  rows: MatchRow[];
  invoices: ImportInvoiceView[];
  unmatchedInvoiceIds: number[];
  mapping: ColumnMapping;
  options: MatchOptions;
  totalRows: number;
  counts: Record<MatchConfidence, number>;
}

export interface ConfirmPair {
  invoiceId: number;
  invoiceNumber: string;
  clientName?: string | null;
  dueDate?: string | null;
  paidDate?: string | null;
}

/**
 * Per-run confirm options. Deliberately NOT part of `MatchOptions` and never
 * remembered between imports — a remembered "clear the paid date" would unpay
 * invoices on the next run without anybody asking for it.
 */
export interface ConfirmOptions {
  markUnpaidWhenNoPaidDate: boolean;
}

export const DEFAULT_CONFIRM_OPTIONS: ConfirmOptions = {
  markUnpaidWhenNoPaidDate: false,
};

export type ConfirmOutcome = 'applied' | 'unchanged' | 'not-found' | 'conflict';

export interface ConfirmPairResult {
  invoiceId: number;
  invoiceNumber: string;
  outcome: ConfirmOutcome;
  changedFields: string[];
  detail: string | null;
}

export interface ConfirmResult {
  applied: number;
  unchanged: number;
  failed: number;
  aliasesLearned: number;
  results: ConfirmPairResult[];
}

async function postMultipart<T>(path: string, formData: FormData): Promise<{ data: T }> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: getBaseAuthHeaders(),
    body: formData,
  });

  if (!response.ok) {
    const error: ApiError = await response.json().catch(() => ({
      statusCode: response.status,
      error: 'Unknown Error',
      message: response.statusText,
    }));
    throw new ApiClientError(error);
  }

  return response.json();
}

export async function analyzeImportFile(file: File): Promise<{ data: AnalyzeResult }> {
  const formData = new FormData();
  formData.append('file', file);
  return postMultipart<AnalyzeResult>('/invoices/import/analyze', formData);
}

export async function matchImportFile(
  file: File,
  mapping: ColumnMapping,
  options: MatchOptions
): Promise<{ data: MatchResult }> {
  const formData = new FormData();
  // The file rides along a second time rather than being cached server-side:
  // the import holds no server state between steps (see the backend service
  // header), so a re-upload is what replaces a batch table.
  formData.append('file', file);
  formData.append('mapping', JSON.stringify(mapping));
  formData.append('options', JSON.stringify(options));
  return postMultipart<MatchResult>('/invoices/import/match', formData);
}

export async function confirmImportPairs(
  pairs: ConfirmPair[],
  options: ConfirmOptions = DEFAULT_CONFIRM_OPTIONS
): Promise<{ data: ConfirmResult }> {
  return apiPost('/invoices/import/confirm', { pairs, options });
}
